import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumnConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { GoogleFontIcon, isNotNullOrUndefined } from '@asset-metrix/portal-ui';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';

import {
  CustomTableFilterQueryKey,
  fetchCustomTableFilterData,
} from '../../../api/portalGateway/customTableFilterData';
import { useStore } from '../../../store';
import {
  useFilterSelections,
  useFilterSelectionsForQuery,
} from '../../../store/hooks/useFilterSelections';
import { prependRoleId } from '../../../utils/prependRoleId';
import { Filter } from '../Filter';
import { validateSingleFilter } from '../validateFilters';

import styles from '../filters.module.scss';

type ColumnFilterConfig = Pick<TableColumnConfigDto, 'id' | 'customFilter'>;
type CustomTableFiltersProps = {
  activeCustomFilterIds: string[];
  columnConfigs: ColumnFilterConfig[];
  pageFilterInstanceId: string;
  widgetId: string;
  widgetFilterInstanceId: string;
};

export const CustomTableFilters = ({
  activeCustomFilterIds,
  columnConfigs,
  pageFilterInstanceId,
  widgetId,
  widgetFilterInstanceId,
}: CustomTableFiltersProps) => {
  const { t } = useTranslation();
  const activeFilterConfigs = useMemo(
    () =>
      columnConfigs
        .filter((c) => activeCustomFilterIds.includes(c.id))
        .map((c) => c.customFilter)
        .filter(isNotNullOrUndefined),
    [activeCustomFilterIds, columnConfigs]
  );

  const [
    resetFilterSelection,
    isPageFilterLoading,
    setSingleFilterData,
    pageFilterLoaded,
  ] = useStore(
    useShallow((state) => [
      state.resetFilterSelection,
      state.isPageFilterLoading,
      state.setSingleFilterData,
      state.filterData[pageFilterInstanceId]?.meta?.loaded,
    ])
  );
  const pageFilterSelections = useFilterSelections(pageFilterInstanceId);
  const selection = useFilterSelectionsForQuery(widgetFilterInstanceId);

  const { data, isFetching: isCustomQueryFetching } = useQuery({
    queryKey: prependRoleId<CustomTableFilterQueryKey>([
      'customQueryFilters',
      widgetId,
      activeFilterConfigs.map((el) => el.key),
      pageFilterSelections,
      selection,
    ]),
    queryFn: fetchCustomTableFilterData,
    enabled: pageFilterLoaded ?? false,
  });

  const isFetching =
    isCustomQueryFetching || isPageFilterLoading || pageFilterLoaded !== true;

  useEffect(() => {
    if (data && data.data !== null && !isCustomQueryFetching) {
      for (const filterConfig of activeFilterConfigs) {
        const validatedFilter = validateSingleFilter(
          filterConfig,
          data.data[filterConfig.key]
        );
        setSingleFilterData(
          widgetFilterInstanceId,
          filterConfig.key,
          validatedFilter
        );
      }
    }
  }, [
    widgetFilterInstanceId,
    data,
    setSingleFilterData,
    isCustomQueryFetching,
    activeFilterConfigs,
  ]);

  return activeFilterConfigs.length > 0
    ? activeFilterConfigs.map((c, index) => {
        return (
          <div className={styles['pagefilter__wrapper']} key={c.key}>
            <div
              className={clsx(
                styles['pagefilter__filter'],
                c.display === 'range-date-picker' &&
                  styles['pagefilter__filter-range']
              )}
            >
              <Filter
                filterConfig={c}
                entityId={widgetFilterInstanceId}
                isFetching={isFetching}
              />
            </div>
            {index + 1 === activeFilterConfigs.length && (
              <GoogleFontIcon
                title={t('app.pageFilter.reset')}
                onClick={() => resetFilterSelection(widgetFilterInstanceId)}
                solid={true}
                className={styles['resetIcon']}
                name="filter_alt_off"
              />
            )}
          </div>
        );
      })
    : null;
};
